/**
 * The institute page's address.
 *
 *   /admin_dashboard_page/institutes-new/<institute-name-slug>/<encoded id>
 *
 * The name is there so a link reads as the institute it opens; the id is
 * base64url-encoded, the same scheme the course and learning routes use, so the
 * raw database id is not on show. The slug is decoration only - the page is
 * found by the id, and a renamed institute's old links keep working.
 *
 * Links from before this shape, `/admin_dashboard_page/institutes-new/<raw id>`,
 * still resolve: an all-digit segment is taken as a raw id. No encoded id is
 * ever all digits (base64 of a digit string starts with M, N or O), so the two
 * cannot be confused.
 *
 * Everything that reads the institute id from the address must go through
 * `useRouteInstituteId` or `instituteIdFromPath` - both hand back the plain id,
 * which is what the API, the `institute_<id>_*` storage keys and the tab state
 * all expect.
 */

import { useParams } from "react-router-dom";

export const INSTITUTE_COMPONENT_ID = "institutes-new";

/** base64url, no padding: 1 → "MQ". */
export const encodeRouteId = (id: string | number): string =>
  btoa(String(id)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");

/**
 * The plain numeric id from an address segment - encoded, or a raw legacy id.
 * `undefined` for anything else (a slug, a hand-edited or truncated value).
 */
export const decodeRouteId = (value?: string | null): string | undefined => {
  const raw = String(value ?? "").trim();
  if (!raw) return undefined;
  if (/^\d+$/.test(raw)) return raw;
  try {
    let base64 = raw.replace(/-/g, "+").replace(/_/g, "/");
    while (base64.length % 4 !== 0) base64 += "=";
    const decoded = atob(base64);
    return /^\d+$/.test(decoded) ? decoded : undefined;
  } catch {
    return undefined;
  }
};

/** "Rajarajeshwari colleage of Engineering" → "rajarajeshwari-colleage-of-engineering". */
export const slugifyInstituteName = (name?: string | null): string =>
  String(name ?? "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "institute";

/** The canonical path for an institute's page. */
export const buildInstitutePath = (id: string | number, name?: string | null): string =>
  `/admin_dashboard_page/${INSTITUTE_COMPONENT_ID}/${slugifyInstituteName(name)}/${encodeRouteId(id)}`;

/**
 * The institute id in an institute page's path, decoded, or `undefined`.
 * Accepts both `/institutes-new/<slug>/<encoded>` and the legacy
 * `/institutes-new/<raw id>`.
 */
export const instituteIdFromPath = (
  pathname: string = typeof window !== "undefined" ? window.location.pathname : "",
): string | undefined => {
  const match = pathname.match(/\/institutes-new\/([^/]+)(?:\/([^/]+))?\/?$/);
  if (!match) return undefined;
  return decodeRouteId(match[2] ?? match[1]);
};

/** `instituteIdFromPath` for components rendered under the admin dashboard route. */
export function useRouteInstituteId(): string | undefined {
  const { instituteId } = useParams();
  return decodeRouteId(instituteId);
}
